import React from 'react'

import { useForm, Controller } from 'react-hook-form'

import { CustomButton } from '@components/CustomButton'
import { FormInput } from '@components/Form/FormInput'
import { FormSelect } from '@components/Form/FormSelect'
import { FormSwitch } from '@components/Form/FormSwitch'
import { FormTextArea } from '@components/Form/FormTextArea'
import { FormHeader } from '@containers/Headers/FormHeader'
import { useFindBarriersCodes } from '@hooks/barriers/find'
import { useUtils } from '@hooks/utils'
import { IBarrier } from '@interfaces/task/barrier'

import { TBarrierForm, useBarrierForm } from './schema'
import { BottomContent } from './styles'

interface IProps {
  onSave: (data: TBarrierForm) => void
  onClose: () => void
  onDelete?: () => void
  barrier?: IBarrier
}

export function BarrierForm({ onClose, onDelete, onSave, barrier }: IProps) {
  const { sortParams } = useUtils()
  const { getResolvedBarrierSchema } = useBarrierForm()

  const { data: rawBarriersCodes } = useFindBarriersCodes()

  const barriersCodes =
    rawBarriersCodes?.sort((a, b) =>
      sortParams(a.description, b.description),
    ) ?? []

  const barrierCodesOptions = barriersCodes
    ?.filter((code) => code.isActive)
    .map((code) => {
      return {
        label: code.description,
        value: code.id,
      }
    })

  const defaultValues = {
    barrierCodeId: barrier?.barrierCodeId,
    hoursLost: barrier?.hoursLost,
    wasCausedByPreviousShift: barrier?.wasCausedByPreviousShift ?? false,
    comment: '',
  }

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<TBarrierForm>({
    resolver: getResolvedBarrierSchema(!barrier?.id),
    defaultValues,
  })

  return (
    <FormHeader
      onPressLeft={onClose}
      leftLabel="Close"
      onPressRight={handleSubmit(onSave)}
      isRightButtonDisabled={!isDirty}
      rightLabel="Save"
      title={barrier?.id ? 'Edit Barrier' : 'Add Barrier'}
    >
      <div>
        <Controller
          control={control}
          name="barrierCodeId"
          render={({
            field: { onChange, value },
            fieldState: { error },
            formState: { defaultValues },
          }) => (
            <FormSelect
              defaultValue={defaultValues?.barrierCodeId?.toString()}
              isRequired
              mb={4}
              placeholder="Please select a barrier code"
              label="Barrier Code "
              items={barrierCodesOptions}
              errorMessage={error?.message}
              onValueChange={(value) => onChange(Number(value))}
              selectedValue={value?.toString()}
            />
          )}
        />

        <Controller
          control={control}
          name="hoursLost"
          render={({
            field: { onChange, value },
            fieldState: { error },
            formState: { defaultValues },
          }) => (
            <FormInput
              isRequired
              defaultValue={defaultValues?.hoursLost?.toString()}
              placeholder="Enter number of hours lost by this barrier"
              label="Hours lost "
              errorMessage={error?.message}
              onChange={(e) => onChange(e.target.value)}
              value={value?.toString()}
            />
          )}
        />

        <Controller
          control={control}
          name="wasCausedByPreviousShift"
          render={({ field: { value, onChange } }) => (
            <FormSwitch
              mb={4}
              label="Caused by previous shift?"
              onToggle={onChange}
              value={value}
            />
          )}
        />

        {!barrier?.id && (
          <Controller
            control={control}
            name="comment"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormTextArea
                isRequired
                placeholder="Enter your comments"
                label="Comments "
                errorMessage={error?.message}
                onChange={(e) => onChange(e.target.value)}
                value={value}
              />
            )}
          />
        )}
      </div>
      {onDelete && (
        <BottomContent>
          <CustomButton onPress={onDelete}>Delete Barrier</CustomButton>
        </BottomContent>
      )}
    </FormHeader>
  )
}
